import {
  INode,
  serializeNodeWithId,
  transformAttribute,
  MaskInputOptions,
  BlockElement,
} from '@xysfe/memento-snapshot';
import {
  mutationRecord,
  blockClass,
  asyncClass,
  mutationCallBack,
  textCursor,
  attributeCursor,
  removedNodeMutation,
  addedNodeMutation,
} from '../types';
import { mirror, isBlocked, isAncestorRemoved } from '../utils';

const moveKey = (id: number, parentId: number) => `${id}@${parentId}`;
function isINode(n: Node | INode): n is INode {
  return '__sn' in n;
}

type DoubleLinkedListNode = {
  previous: DoubleLinkedListNode | null;
  next: DoubleLinkedListNode | null;
  value: NodeInLinkedList;
};
type NodeInLinkedList = Node & {
  __ln: DoubleLinkedListNode;
};

function isNodeInLinkedList(n: Node | NodeInLinkedList): n is NodeInLinkedList {
  return '__ln' in n;
}

class DoubleLinkedList {
  public length = 0;
  public head: DoubleLinkedListNode | null = null;

  public get(position: number) {
    if (position >= this.length) {
      throw new Error('Position outside of list range');
    }

    let current = this.head;
    for (let index = 0; index < position; index++) {
      current = current?.next || null;
    }
    return current;
  }

  public addNode(n: Node) {
    const node: DoubleLinkedListNode = {
      value: n as NodeInLinkedList,
      previous: null,
      next: null,
    };
    (n as NodeInLinkedList).__ln = node;
    if ((n as any).previousSibling && isNodeInLinkedList((n as any).previousSibling)) {
      const current = (n as any).previousSibling.__ln.next;
      node.next = current;
      node.previous = (n as any).previousSibling.__ln;
      (n as any).previousSibling.__ln.next = node;
      if (current) {
        current.previous = node;
      }
    } else if ((n as any).nextSibling && isNodeInLinkedList((n as any).nextSibling) && (n as any).nextSibling.__ln.previous) {
      const current = (n as any).nextSibling.__ln.previous;
      node.previous = current;
      node.next = (n as any).nextSibling.__ln;
      (n as any).nextSibling.__ln.previous = node;
      if (current) {
        current.next = node;
      }
    } else {
      if (this.head) {
        this.head.previous = node;
      }
      node.next = this.head;
      this.head = node;
    }
    this.length++;
  }

  public removeNode(n: NodeInLinkedList) {
    const current = n.__ln;
    if (!this.head) {
      return;
    }

    if (!current.previous) {
      this.head = current.next;
      if (this.head) {
        this.head.previous = null;
      }
    } else {
      current.previous.next = current.next;
      if (current.next) {
        current.next.previous = current.previous;
      }
    }
    if (n.__ln) {
      delete n.__ln;
    }
    this.length--;
  }
}

/**
 * controls behaviour of a MutationObserver
 */
export default class MutationBuffer {
  private texts: textCursor[] = [];
  private attributes: attributeCursor[] = [];
  private removes: removedNodeMutation[] = [];
  private adds: addedNodeMutation[] = [];

  private movedMap: Record<string, true> = {};

  /**
   * the browser MutationObserver emits multiple mutations after
   * a delay for performance reasons, making tracing added nodes hard
   * in our `processMutations` callback function.
   * For example, if we append an element el_1 into body, and then append
   * another element el_2 into el_1, these two mutations may be passed to the
   * callback function together when the two operations were done.
   * Generally we need to trace child nodes of newly added nodes, but in this
   * case if we count el_2 as el_1's child node in the first mutation record,
   * then we will count el_2 again in the second mutation record which was
   * duplicated.
   * To avoid of duplicate counting added nodes, we use a Set to store
   * added nodes and its child nodes during iterate mutation records. Then
   * collect added nodes from the Set which have no duplicate copy. But
   * this also causes newly added nodes will not be serialized with id ASAP,
   * which means all the id related calculation should be lazy too.
   */
  private addedSet = new Set<Node>();
  private movedSet = new Set<Node>();
  private droppedSet = new Set<Node>();

  private emissionCallback: mutationCallBack;
  private blockClass: blockClass;
  private blockElements: BlockElement[];
  private asyncClass?: asyncClass;
  private inlineStylesheet: boolean;
  private maskInputOptions: MaskInputOptions;

  constructor(
    cb: mutationCallBack,
    blockClass: blockClass,
    inlineStylesheet: boolean,
    maskInputOptions: MaskInputOptions,
    blockElements: BlockElement[] = [],
    asyncClass?: asyncClass,
  ) {
    this.blockClass = blockClass;
    this.blockElements = blockElements;
    this.asyncClass = asyncClass;
    this.inlineStylesheet = inlineStylesheet;
    this.maskInputOptions = maskInputOptions;
    this.emissionCallback = cb;
  }

  public processMutations = (mutations: mutationRecord[]) => {
    this.reset();

    mutations.forEach(this.processMutation);

    const texts = this.texts;
    const attributes = this.attributes;
    const removes = this.removes;
    const adds = this.adds;
    const addedSet = this.addedSet;
    const movedSet = this.movedSet;
    const droppedSet = this.droppedSet;
    const changedSet = [...addedSet, ...movedSet];
    const asyncMutations: Array<Promise<any>> = [];
    const addList = new DoubleLinkedList()
    const getNextId = (n: Node): number | null => {
      let nextId =
        (n as any).nextSibling && mirror.getId(((n as any).nextSibling as unknown) as INode);
      if (nextId === -1 && isBlocked((n as any).nextSibling, this.blockClass)) {
        nextId = null;
      }
      return nextId;
    };
    let asyncPush = false;

    if (this.asyncClass) {
      for (const item of changedSet) {
        if (isBlocked(item.parentNode, this.asyncClass)) {
          asyncPush = true;
          break;
        }
      }
      if (!asyncPush) {
        for (const attribute of attributes) {
          if (isBlocked(attribute.node.parentNode, this.asyncClass)) {
            asyncPush = true;
            break;
          }
        }
      }
    }

    /**
     * Sometimes child node may be pushed before its newly added
     * parent, so we init a queue to store these nodes.
     */
    const addQueue: Node[] = [];
    const _pushAdd = (n: Node) => {
      if (!n.parentNode) {
        return;
      }
      const parentId = mirror.getId((n.parentNode as Node) as INode);
      // #fix https://developer.mozilla.org/zh-CN/docs/Web/API/Node/nextSibling
      // const previousId =
      //   ((n as any).previousSibling && mirror.getId(((n as any).previousSibling as unknown) as INode))
      // || (n.previousSibling && mirror.getId((n.previousSibling as unknown) as INode));
      const nextId = getNextId(n)
      // || (n.nextSibling && mirror.getId((n.nextSibling as unknown) as INode));
      if (parentId === -1 || nextId === -1) {
        return addList.addNode(n)
        // return addQueue.push(n);
      }
      adds.push({
        parentId,
        nextId,
        node: serializeNodeWithId(
          n,
          document,
          mirror.map,
          this.blockClass,
          true,
          this.inlineStylesheet,
          this.maskInputOptions,
          this.blockElements,
        )!,
      });
    };

    const pushAdd = (n: Node) => {
      if (asyncPush) {
        asyncMutations.push(new Promise((resolve) => {
          this.requestIdleCallback(() => {
            _pushAdd(n);
            resolve();
          });
        }));
      } else {
        _pushAdd(n);
      }
    };

    for (const n of movedSet) {
      pushAdd(n);
    }

    for (const n of addedSet) {
      if (
        !isAncestorInSet(droppedSet, n) &&
        !isParentRemoved(removes, n)
      ) {
        pushAdd(n);
      } else if (isAncestorInSet(movedSet, n)) {
        pushAdd(n);
      } else {
        droppedSet.add(n);
      }
    }

    const handleQueue = () => {
      let candidate: DoubleLinkedListNode | null = null
      while (addList.length) {
        let node: DoubleLinkedListNode | null = null
        
        if (candidate) {
          const parentId = mirror.getId(
            (candidate.value.parentNode as Node) as INode
          )
          const nextId = getNextId(candidate.value)
          if (parentId !== -1 && nextId !== -1) {
            node = candidate
          }
        }
        
        // 从后往前找，找到可以操作的元素，找不到则退出while循环
        if (!node) {
          for (let index = addList.length - 1; index >= 0; index--) {
            const _node = addList.get(index)
            
            const parentId = mirror.getId(((_node as DoubleLinkedListNode).value.parentNode as Node) as INode)
            const nextId = getNextId((_node as DoubleLinkedListNode).value)
            if (parentId !== -1 && nextId !== -1) {
              node = _node
              break
            }
          }
        }
        if (!node) {
          while (addList.head) {
            addList.removeNode(addList.head.value);
          }
          break
        }
        
        candidate = node.previous
        addList.removeNode(node.value)
        _pushAdd(node.value)
      }
    };

    if (asyncPush) {
      const timestamp = Date.now();
      Promise.all(asyncMutations).then(() => {
        handleQueue();
        this.emit(texts, attributes, removes, adds, timestamp);
      });
    } else {
      handleQueue();
      this.emit(texts, attributes, removes, adds);
    }
  };

  private emit = (texts: textCursor[], attributes: attributeCursor[], removes: removedNodeMutation[], adds: addedNodeMutation[], t?: number) => {
    const payload = {
      texts: texts
        .map((text) => ({
          id: mirror.getId(text.node as INode),
          value: text.value,
        }))
        // text mutation's id was not in the mirror map means the target node has been removed
        .filter((text) => mirror.has(text.id)),
      attributes: attributes
        .map((attribute) => ({
          id: mirror.getId(attribute.node as INode),
          attributes: attribute.attributes,
        }))
        // attribute mutation's id was not in the mirror map means the target node has been removed
        .filter((attribute) => mirror.has(attribute.id)),
      removes,
      adds,
      timestamp: t,
    };
    // payload may be empty if the mutations happened in some blocked elements
    if (
      !payload.texts.length &&
      !payload.attributes.length &&
      !payload.removes.length &&
      !payload.adds.length
    ) {
      return;
    }
    this.emissionCallback(payload);
  };

  private processMutation = (m: mutationRecord) => {
    switch (m.type) {
      case 'characterData': {
        const value = m.target.textContent;
        if (!isBlocked(m.target, this.blockClass, this.blockElements) && value !== m.oldValue) {
          this.texts.push({
            value,
            node: m.target,
          });
        }
        break;
      }
      case 'attributes': {
        const value = (m.target as HTMLElement).getAttribute(m.attributeName!);
        if (isBlocked(m.target, this.blockClass, this.blockElements) || value === m.oldValue) {
          return;
        }
        let item: attributeCursor | undefined = this.attributes.find(
          (a) => a.node === m.target,
        );
        if (!item) {
          item = {
            node: m.target,
            attributes: {},
          };
          this.attributes.push(item);
        }
        // overwrite attribute if the mutations was triggered in same time
        item.attributes[m.attributeName!] = transformAttribute(
          document,
          m.attributeName!,
          value!,
        );
        // 冗余静态资源方便后端提取
        const tagName = (m.target as HTMLElement).tagName.toLowerCase().trim();
        const isStaticRes = (tagName === 'img' && m.attributeName === 'src')
                        || (tagName === 'link' && m.attributeName === 'href');
        const rel = ((m.target as HTMLElement) as any).rel;
        const isPreload =  rel === 'dns-prefetch' || rel === 'preload';
        if (isStaticRes && !isPreload && !/^data:image/i.test(item.attributes[m.attributeName!] as string)) {
          item.attributes[`_memimage_${m.attributeName!}`] = item.attributes[m.attributeName!];
        }
        break;
      }
      case 'childList': {
        m.addedNodes.forEach((n) => this.genAdds(n, m.target));
        m.removedNodes.forEach((n) => {
          const nodeId = mirror.getId(n as INode);
          const parentId = mirror.getId(m.target as INode);
          if (
            isBlocked(n, this.blockClass, this.blockElements) ||
            isBlocked(m.target, this.blockClass, this.blockElements)
          ) {
            return;
          }
          // removed node has not been serialized yet, just remove it from the Set
          if (this.addedSet.has(n)) {
            deepDelete(this.addedSet, n);
            this.droppedSet.add(n);
          } else if (this.addedSet.has(m.target) && nodeId === -1) {
            /**
             * If target was newly added and removed child node was
             * not serialized, it means the child node has been removed
             * before callback fired, so we can ignore it because
             * newly added node will be serialized without child nodes.
             * TODO: verify this
             */
          } else if (isAncestorRemoved(m.target as INode)) {
            /**
             * If parent id was not in the mirror map any more, it
             * means the parent node has already been removed. So
             * the node is also removed which we do not need to track
             * and replay.
             */
          } else if (
            this.movedSet.has(n) &&
            this.movedMap[moveKey(nodeId, parentId)]
          ) {
            deepDelete(this.movedSet, n);
          } else {
            this.removes.push({
              parentId,
              id: nodeId,
            });
          }
          mirror.removeNodeFromMap(n as INode);
        });
        break;
      }
      default:
        break;
    }
  }

  private genAdds = (n: Node | INode, target?: Node | INode) => {
    // 20200909 by koma 所有节点都必须加入 否则基于该节点的previous节点录制时无法生成，回放时无法插入
    // if (isBlocked(n, this.blockClass, this.blockElements)) {
    //   return;
    // }
    if (isBlocked(n.parentNode, this.blockClass, this.blockElements)) {
      return;
    }
    if (isINode(n)) {
      this.movedSet.add(n);
      let targetId: number | null = null;
      if (target && isINode(target)) {
        targetId = target.__sn.id;
      }
      if (targetId) {
        this.movedMap[moveKey(n.__sn.id, targetId)] = true;
      }
    } else {
      this.addedSet.add(n);
      this.droppedSet.delete(n);
    }
    n.childNodes.forEach((childN) => this.genAdds(childN));
  };

  private reset = () => {
    // reset
    this.texts = [];
    this.attributes = [];
    this.removes = [];
    this.adds = [];
    this.addedSet = new Set<Node>();
    this.movedSet = new Set<Node>();
    this.droppedSet = new Set<Node>();
    this.movedMap = {};
  }

  private requestIdleCallback = (callback: Function, options?: { timeout: number }) => {
    if ( (window as any).requestIdleCallback) {
      (window as any).requestIdleCallback(callback, options);
    } else {
      const { timeout } = options || { timeout: 0 };
      (window as any).setTimeout(callback, timeout);
    }
  }
}

/**
 * Some utils to handle the mutation observer DOM records.
 * It should be more clear to extend the native data structure
 * like Set and Map, but currently Typescript does not support
 * that.
 */
function deepDelete(addsSet: Set<Node>, n: Node) {
  addsSet.delete(n);
  n.childNodes.forEach((childN) => deepDelete(addsSet, childN));
}

function isParentRemoved(removes: removedNodeMutation[], n: Node): boolean {
  const { parentNode } = n;
  if (!parentNode) {
    return false;
  }
  const parentId = mirror.getId((parentNode as Node) as INode);
  if (removes.some((r) => r.id === parentId)) {
    return true;
  }
  return isParentRemoved(removes, parentNode);
}

function isAncestorInSet(set: Set<Node>, n: Node): boolean {
  const { parentNode } = n;
  if (!parentNode) {
    return false;
  }
  if (set.has(parentNode)) {
    return true;
  }
  return isAncestorInSet(set, parentNode);
}
